import { defineStore } from "pinia";
import axios from "axios";

function authHeader() {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}` };
}

export const useOpenFinanceStore = defineStore("open_finance", {
  state: () => ({
    syncStatus: {
      // Keyed by bank account id: { id, name, balance_updated_at, last_sync_requested_at }
      data: {},
      loading: false,
      loaded: false,
    },
  }),

  getters: {
    /**
     * Returns seconds remaining on the 4-hour cooldown for a given account.
     * Returns 0 if cooldown has expired or was never set.
     */
    cooldownSecondsFor: (state) => (bankAccountId) => {
      const account = state.syncStatus.data[bankAccountId];
      if (!account?.last_sync_requested_at) return 0;
      const cooldownEndsAt =
        new Date(account.last_sync_requested_at).getTime() +
        4 * 60 * 60 * 1000; // +4 hours
      const remaining = Math.floor((cooldownEndsAt - Date.now()) / 1000);
      return remaining > 0 ? remaining : 0;
    },
  },

  actions: {
    getConnectToken() {
      return axios
        .get("/open_finance/connect_token", { headers: authHeader() })
        .then((response) => Promise.resolve(response))
        .catch((error) => Promise.reject(error));
    },

    saveAccount(payload) {
      return axios
        .post("/open_finance/account", payload, { headers: authHeader() })
        .then((response) => Promise.resolve(response))
        .catch((error) => Promise.reject(error));
    },

    async getSyncStatus() {
      this.syncStatus.loading = true;
      try {
        const { data } = await axios.get("/open_finance/sync_status", {
          headers: authHeader(),
        });
        this.syncStatus.data = Object.fromEntries(
          data.accounts.map((a) => [a.id, a])
        );
        this.syncStatus.loaded = true;
      } finally {
        this.syncStatus.loading = false;
      }
    },

    async triggerSync(bankAccountId) {
      const { data } = await axios.post(
        `/open_finance/sync/${bankAccountId}`,
        {},
        { headers: authHeader() }
      );
      // Immediate refresh so sibling accounts reflect the cooldown right away
      await this.getSyncStatus();
      // Second refresh after a delay so balance_updated_at picks up the new data
      setTimeout(() => this.getSyncStatus(), 5000);
      return data;
    },
  },
});
